import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Smartphone, Camera, Lock, Sparkles, CheckCircle2, ArrowRight, Home, ExternalLink } from "lucide-react";

/**
 * PayPunch Kiosk Pro installation walkthrough.
 * URL: /kiosk-install
 *
 * Guides the store owner through:
 *   1. Adding PayPunch Kiosk Pro to the iPad's home screen as an app
 *   2. Enrolling employees at /face-enroll
 *   3. Locking the iPad with iOS Guided Access
 */
export default function KioskInstallPage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0F1013] via-[#1A1620] to-[#2A0E1E] text-white p-4">
      <div className="max-w-3xl mx-auto py-8">
        {/* Hero */}
        <div className="text-center mb-10">
          <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-[#A61B1B] to-[#5B0F0F] mx-auto flex items-center justify-center mb-4 shadow-2xl">
            <Lock className="w-10 h-10 text-amber-300" />
          </div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-amber-300 font-bold">Powered by AV Tech</div>
          <h1 className="text-4xl font-black mt-1">Install PayPunch Kiosk Pro</h1>
          <p className="text-white/60 mt-2 text-lg">Turn your iPad into a locked, face-verified time clock in 5 minutes.</p>
        </div>

        {/* Steps */}
        <div className="space-y-4">
          <Step
            n={1}
            title="On the iPad, open Safari and go to this exact URL"
            body={
              <div className="mt-2 bg-white/5 border border-white/10 rounded-xl p-3 flex items-center justify-between font-mono text-amber-300 text-sm break-all">
                <span data-testid="kiosk-url">ordermeatonoakton.com/kiosk-secure</span>
                <a
                  href="/kiosk-secure"
                  className="text-amber-300 hover:text-amber-200"
                  target="_blank"
                  rel="noreferrer"
                >
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            }
          />

          <Step
            n={2}
            title='Tap the Share button, then "Add to Home Screen"'
            body={
              <div className="mt-2 text-white/70 text-sm space-y-1">
                <p>• Safari → Share icon (bottom bar, arrow-up-out-of-box)</p>
                <p>• Scroll → tap <b className="text-amber-300">Add to Home Screen</b></p>
                <p>• Name: <b>PayPunch</b> → tap <b>Add</b></p>
                <p className="text-xs text-white/50 mt-1">
                  A red PayPunch icon appears on the home screen — that's your kiosk app.
                </p>
              </div>
            }
          />

          <Step
            n={3}
            title="Enroll every employee (one-time, 10-15 min each)"
            body={
              <div className="mt-2 space-y-2">
                <p className="text-white/70 text-sm">
                  Before the kiosk works, each employee needs a face baseline. Do this once per employee.
                </p>
                <Link to="/face-enroll">
                  <Button className="bg-amber-400 text-black hover:bg-amber-500 font-bold" data-testid="go-enroll">
                    Open Face Enrollment <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <p className="text-xs text-white/50">Master PIN required. Capture 6 photos per employee across different poses.</p>
              </div>
            }
          />

          <Step
            n={4}
            title="Test with your master PIN (83528)"
            body={
              <div className="mt-2 text-white/70 text-sm space-y-1">
                <p>Open the PayPunch app icon from the home screen → tap START.</p>
                <p>Enter <b className="text-amber-300 font-mono">83528</b>, tap GO, pick a punch type, look at the camera.</p>
                <p className="text-xs text-white/50 mt-1">
                  Note: You must enroll code 83528 first (or any real employee code you want to test).
                </p>
                <Link to="/kiosk-secure">
                  <Button className="mt-2 bg-white/10 text-white hover:bg-white/20" data-testid="go-kiosk">
                    Open Kiosk Now <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            }
          />

          <Step
            n={5}
            title="Lock the iPad with Guided Access (built-in iOS feature)"
            body={
              <div className="mt-2 text-white/70 text-sm space-y-1">
                <p>1. iPad <b>Settings → Accessibility → Guided Access</b> → ON</p>
                <p>2. Tap <b>Passcode Settings</b> → set a 6-digit PIN (keep it secret, this unlocks the iPad from kiosk)</p>
                <p>3. Optional: <b>Face ID</b> → ON (so you can unlock by looking at it)</p>
                <p>4. Open the PayPunch app → <b>triple-click the top button</b> (or home button on older iPads) → tap <b>Start</b></p>
                <p className="text-emerald-300 text-xs mt-2 flex items-start gap-1">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
                  iPad is now locked to PayPunch. No one can leave, switch apps, or open Safari without your PIN.
                </p>
              </div>
            }
          />

          <Step
            n={6}
            title="Mount the iPad on the wall"
            body={
              <div className="mt-2 text-white/70 text-sm space-y-1">
                <p>Any generic <b>iPad wall mount</b> works ($30-60 on Amazon).</p>
                <p>Mount at ~65" high (adult eye level). Angle 5° upward.</p>
                <p>Keep it plugged in — <b>Settings → Display → Auto-Lock → Never</b>.</p>
              </div>
            }
          />
        </div>

        {/* Quick reference */}
        <Card className="bg-amber-500/10 border-amber-400/30 p-5 mt-8 text-white">
          <h3 className="font-black text-lg text-amber-300 flex items-center gap-2">
            <Sparkles className="w-5 h-5" /> Quick Reference
          </h3>
          <div className="grid grid-cols-2 gap-3 mt-3 text-sm">
            <RefItem label="Kiosk URL" value="/kiosk-secure" />
            <RefItem label="Enrollment URL" value="/face-enroll" />
            <RefItem label="Master PIN" value="83528 · 4817 · 1111" />
            <RefItem label="Exit Kiosk" value="tap top-right corner 5×" />
          </div>
        </Card>

        <div className="mt-8 flex justify-center gap-3">
          <Link to="/timecard">
            <Button variant="outline" className="border-white/20 text-white hover:bg-white/10" data-testid="install-back">
              <Home className="w-4 h-4 mr-1" /> Master Dashboard
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}

function Step({ n, title, body }) {
  return (
    <Card className="bg-white/5 border-white/10 p-5">
      <div className="flex gap-4">
        <div className="w-10 h-10 shrink-0 rounded-full bg-amber-400/20 border border-amber-400/40 flex items-center justify-center text-amber-300 font-black">
          {n}
        </div>
        <div className="flex-1">
          <h2 className="font-black text-lg">{title}</h2>
          {body}
        </div>
      </div>
    </Card>
  );
}

function RefItem({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-white/50 font-bold">{label}</div>
      <div className="font-mono font-bold">{value}</div>
    </div>
  );
}
